import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Col, Empty, Form, Input, Modal, Row, Spin, Switch, Tag, Typography, message } from "antd";
import { PlusOutlined, SaveOutlined } from "@ant-design/icons";

import { scenarioContextApi } from "../../../../../api/scenarioContextApi";
import ProductListEditor from "../../../../agent/scenarioContext/components/ProductListEditor";
import { getStandardCostTypeLabel } from "../../../constants";

const { Text } = Typography;
const { TextArea } = Input;

const PRODUCT_CONTEXT = {
    entryType: "KNOWLEDGE",
    knowledgeDomain: "ap_products",
    agentName: "ap_invoice_parser_agent",
};

const DEFAULT_UNIT = "个";

const normalizeText = (value) => String(value ?? "").trim();

const getErrorMessage = (error, fallbackMessage) => error.response?.data?.detail || error.response?.data?.message || error.message || fallbackMessage;

const getSupplierDisplay = (conditions = {}) => {
    if (conditions.supplier_display_name) return conditions.supplier_display_name;
    if (conditions.supplier_code && conditions.supplier_name) return `${conditions.supplier_code} - ${conditions.supplier_name}`;
    return conditions.supplier_name || conditions.supplier_id || "-";
};

const getLabelValue = (labels = {}, keys = []) => {
    for (const key of keys) {
        if (labels[key]) return labels[key];
    }
    return undefined;
};

const normalizeProductsForEdit = (products = []) => products.map((product) => ({
    ...product,
    unit: normalizeText(product.unit) || DEFAULT_UNIT,
    airport_iata_code: normalizeText(product.airport_iata_code).toUpperCase(),
}));

const normalizeProductForSubmit = (product) => {
    const airportCode = normalizeText(product.airport_iata_code).toUpperCase();
    const nextProduct = {
        product_code: normalizeText(product.product_code),
        name: normalizeText(product.name),
        cost_type: product.cost_type,
        unit: normalizeText(product.unit) || DEFAULT_UNIT,
        recognition_hints: normalizeText(product.recognition_hints),
    };

    if (airportCode) {
        nextProduct.airport_iata_code = airportCode;
    }

    return nextProduct;
};

const validateProductList = (_, value) => {
    const products = value?.products || [];
    const seenCodes = new Set();

    for (const product of products) {
        const productCode = normalizeText(product.product_code);
        const productName = normalizeText(product.name);
        const unit = normalizeText(product.unit) || DEFAULT_UNIT;
        const recognitionHints = normalizeText(product.recognition_hints);

        if (!productCode || !productName || !product.cost_type || !unit || !recognitionHints) {
            return Promise.reject(new Error("请补全产品编码、产品名称、费用类型、单位和识别提示"));
        }
        if (seenCodes.has(productCode)) {
            return Promise.reject(new Error(`产品编码 "${productCode}" 重复`));
        }
        seenCodes.add(productCode);

        const airportCode = normalizeText(product.airport_iata_code).toUpperCase();
        if (airportCode && !/^[A-Z]{3}$/.test(airportCode)) {
            return Promise.reject(new Error(`机场代码 "${airportCode}" 不是 3 位 IATA 代码`));
        }
    }

    return Promise.resolve();
};

const buildFormValues = (sourceRecord) => ({
    description: sourceRecord?.description || "",
    is_active: sourceRecord?.is_active ?? true,
    json_content: {
        products: normalizeProductsForEdit(sourceRecord?.json_content?.products || []),
    },
});

const ProductMaintenanceModal = ({ visible, onClose, onSaved, predefinedFilters = {}, predefinedFilterLabels = {} }) => {
    const [form] = Form.useForm();
    const [record, setRecord] = useState(null);
    const [hasDraft, setHasDraft] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const predefinedSupplierId = predefinedFilters?.scenario_supplier_id;
    const predefinedCostType = predefinedFilters?.scenario_standard_cost_type;
    const hasRequiredContext = Boolean(predefinedSupplierId && predefinedCostType);
    const hasEditableContent = Boolean(record || hasDraft);

    const fixedContext = useMemo(() => {
        const conditions = record?.scenario_conditions || {};
        const recordSupplierDisplay = getSupplierDisplay(conditions);
        const supplierLabel = getLabelValue(predefinedFilterLabels, [
            "scenario_supplier_label",
            "scenario_supplier_name",
            "supplierLabel",
            "supplierName",
            "supplier_name",
        ]) || (recordSupplierDisplay === "-" ? undefined : recordSupplierDisplay) || predefinedSupplierId || "-";
        const costTypeLabel = getLabelValue(predefinedFilterLabels, [
            "scenario_standard_cost_type_label",
            "standardCostTypeLabel",
            "standard_cost_type_label",
            "costTypeLabel",
        ]) || getStandardCostTypeLabel(predefinedCostType || conditions.standard_cost_type);

        return { supplierLabel, costTypeLabel };
    }, [predefinedCostType, predefinedFilterLabels, predefinedSupplierId, record]);

    useEffect(() => {
        if (!visible) {
            setRecord(null);
            setHasDraft(false);
            setLoading(false);
            setSaving(false);
            form.resetFields();
            return undefined;
        }

        if (!hasRequiredContext) {
            setRecord(null);
            setHasDraft(false);
            setLoading(false);
            form.resetFields();
            return undefined;
        }

        let cancelled = false;
        const loadRecord = async () => {
            setLoading(true);
            try {
                const response = await scenarioContextApi.getList({
                    page: 1,
                    page_size: 1,
                    agent_name: PRODUCT_CONTEXT.agentName,
                    entry_type: PRODUCT_CONTEXT.entryType,
                    scenario_knowledge_domain: PRODUCT_CONTEXT.knowledgeDomain,
                    scenario_supplier_id: predefinedSupplierId,
                    scenario_standard_cost_type: predefinedCostType,
                });

                if (cancelled) return;
                const nextRecord = response.data?.items?.[0] || null;
                setRecord(nextRecord);
                setHasDraft(Boolean(nextRecord));
                if (nextRecord) {
                    form.setFieldsValue(buildFormValues(nextRecord));
                } else {
                    form.resetFields();
                }
            } catch (error) {
                if (!cancelled) message.error(getErrorMessage(error, "加载产品配置失败"));
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        loadRecord();

        return () => {
            cancelled = true;
        };
    }, [form, hasRequiredContext, predefinedCostType, predefinedSupplierId, visible]);

    const createDraft = () => {
        form.setFieldsValue(buildFormValues(null));
        setHasDraft(true);
    };

    const buildSubmitPayload = (values) => ({
        agent_name: PRODUCT_CONTEXT.agentName,
        entry_type: PRODUCT_CONTEXT.entryType,
        scenario_conditions: {
            supplier_id: String(predefinedSupplierId),
            standard_cost_type: predefinedCostType,
            knowledge_domain: PRODUCT_CONTEXT.knowledgeDomain,
        },
        prompt_text: null,
        json_content: {
            products: (values.json_content?.products || []).map(normalizeProductForSubmit),
        },
        description: normalizeText(values.description),
        is_active: values.is_active !== false,
    });

    const handleSubmit = async () => {
        if (!hasRequiredContext) {
            message.error("缺少供应商或费用类型，无法保存产品配置");
            return;
        }
        if (!hasEditableContent) {
            message.error("请先创建产品配置");
            return;
        }

        try {
            const values = await form.validateFields();
            const payload = buildSubmitPayload(values);
            setSaving(true);

            const response = record?.id
                ? await scenarioContextApi.update({ id: record.id, data: payload })
                : await scenarioContextApi.create(payload);

            if (response.data?.success === false) {
                throw new Error(response.data?.message || "保存产品配置失败");
            }

            const savedRecord = response.data?.data?.scenario_context || { ...record, ...payload };
            message.success(response.data?.message || "产品配置已保存");
            setRecord(savedRecord);
            setHasDraft(true);
            form.setFieldsValue(buildFormValues(savedRecord));
            if (onSaved) onSaved();
        } catch (error) {
            if (error.errorFields) return;
            message.error(getErrorMessage(error, "保存产品配置失败"));
        } finally {
            setSaving(false);
        }
    };

    const renderFixedContext = () => (
        <Card size="small" style={{ marginBottom: 16 }} bodyStyle={{ padding: "12px 16px" }}>
            <Row gutter={[16, 8]}>
                <Col xs={24} md={12}>
                    <Text type="secondary">供应商</Text>
                    <div style={{ marginTop: 4 }}>
                        <Tag color="blue">{fixedContext.supplierLabel}</Tag>
                    </div>
                </Col>
                <Col xs={24} md={12}>
                    <Text type="secondary">费用类型</Text>
                    <div style={{ marginTop: 4 }}>
                        <Tag color="orange">{fixedContext.costTypeLabel}</Tag>
                    </div>
                </Col>
            </Row>
        </Card>
    );

    const renderContent = () => {
        if (!hasEditableContent) {
            return (
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="该供应商该费用类型暂无产品配置"
                >
                    <Button type="primary" icon={<PlusOutlined />} disabled={!hasRequiredContext} onClick={createDraft}>
                        创建产品配置
                    </Button>
                </Empty>
            );
        }

        return (
            <Form form={form} layout="vertical">
                <Card size="small" title="产品配置" style={{ marginBottom: 16 }}>
                    <Row gutter={16}>
                        <Col xs={24} md={8}>
                            <Form.Item name="is_active" label="状态" valuePropName="checked">
                                <Switch checkedChildren="启用" unCheckedChildren="停用" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item name="description" label="说明">
                        <TextArea rows={2} placeholder="可填写产品配置说明" maxLength={300} showCount />
                    </Form.Item>
                </Card>
                <Form.Item name="json_content" label="产品清单" rules={[{ validator: validateProductList }]}>
                    <ProductListEditor />
                </Form.Item>
            </Form>
        );
    };

    return (
        <Modal
            title="产品维护"
            className="fullscreen-modal"
            open={visible}
            onCancel={onClose}
            destroyOnHidden
            width={1200}
            footer={[
                <Button key="close" onClick={onClose}>
                    关闭
                </Button>,
                <Button key="save" type="primary" icon={<SaveOutlined />} loading={saving} disabled={!hasEditableContent || !hasRequiredContext} onClick={handleSubmit}>
                    保存
                </Button>,
            ]}
            styles={{ body: { maxHeight: "calc(100vh - 140px)", overflowY: "auto", padding: "16px 24px" } }}
        >
            {renderFixedContext()}
            {!hasRequiredContext && (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="缺少固定上下文"
                    description="产品维护需要当前供应商和费用类型，当前入口未提供完整预设条件。"
                />
            )}
            {loading ? (
                <div style={{ textAlign: "center", padding: "56px 0" }}>
                    <Spin size="large" />
                    <div style={{ marginTop: 12 }}>
                        <Text type="secondary">正在加载产品配置...</Text>
                    </div>
                </div>
            ) : renderContent()}
        </Modal>
    );
};

export default ProductMaintenanceModal;
